import { PaymentStrategy } from '@ctct/state-server-layer';

import {
  DataPreparationService,
  SecurePayService,
  SingleBillPaymentMethodsService,
  SingleBillStripeElementsService,
} from './services';
import type { Init } from './types';
import { managerHelpers } from '../../helpers';
import type { MergedDataReturnType, MergedHandlersReturnType } from '../../types';
import type { WidgetLifecycle } from '../WidgetLifecycle';
import { WithApi } from '../WithApi';

export type SingleBillPaymentMethodsData = MergedDataReturnType<
  [SingleBillPaymentMethodsService, SecurePayService, SingleBillStripeElementsService]
>;

export type SingleBillPaymentMethodsHandlers = MergedHandlersReturnType<
  [SingleBillPaymentMethodsService, SecurePayService, SingleBillStripeElementsService]
>;

type TApi = ReturnType<SingleBillPaymentMethodsService['api']> &
  ReturnType<DataPreparationService['api']> &
  ReturnType<SingleBillStripeElementsService['handlers']>;

export class SingleBillPaymentMethodsManager extends WithApi<TApi> {
  public singleBillPaymentMethodsInit = ({ pageStore, queries, t, paymentStrategy, flags }: Init) => {
    // Note: flags.shouldIgnoreRatingCall exists for API consistency but SingleBillPaymentMethodsManager
    // doesn't use a rating call - this is different from the regular PaymentMethodsManager
    const [singleBillQuery, partnerNameQuery, isAmexIrAffiliateQuery, singleBillSetupElementQuery] = queries;

    const isStripePayments = paymentStrategy.data === PaymentStrategy.Stripe;

    const setupElementResponse = singleBillSetupElementQuery.data ?? null;

    const pending =
      paymentStrategy.isPending ||
      singleBillQuery.isPending ||
      partnerNameQuery.isPending ||
      isAmexIrAffiliateQuery.isPending ||
      singleBillSetupElementQuery.isFetching;

    const error =
      singleBillQuery.error ||
      partnerNameQuery.error ||
      isAmexIrAffiliateQuery.error ||
      singleBillSetupElementQuery.error;

    if (pending) {
      return {
        status: 'pending',
        data: null,
        handlers: null,
        lifecycle: null,
      } as const;
    }

    if (error) {
      return {
        status: 'error',
        data: error,
        handlers: {
          retry: () => {
            managerHelpers.retryQueries(queries);
          },
        },
        lifecycle: null,
      } as const;
    }

    const paymentMethodsService = new SingleBillPaymentMethodsService(pageStore, singleBillQuery.data);

    const securePayService = new SecurePayService(
      pageStore,
      singleBillQuery.data,
      partnerNameQuery.data,
      isAmexIrAffiliateQuery.data
    );

    const singleBillStripeElementsService = new SingleBillStripeElementsService(
      setupElementResponse,
      pageStore,
      singleBillQuery.data.getCreditCardInfo()?.cardToken ?? null,
      isStripePayments
    );

    const dataPreparationService = new DataPreparationService(
      pageStore,
      singleBillQuery.data,
      paymentMethodsService,
      securePayService,
      singleBillStripeElementsService
    );

    this.initApi({
      ...paymentMethodsService.api(),
      ...dataPreparationService.api(),
      ...singleBillStripeElementsService.handlers(),
    });

    return {
      status: 'loaded',
      data: {
        ...paymentMethodsService.data(),
        ...securePayService.data(),
        ...singleBillStripeElementsService.data(),
      },
      handlers: {
        ...paymentMethodsService.handlers(),
        ...securePayService.handlers(),
        ...singleBillStripeElementsService.handlers(),
      },
      lifecycle: {
        onMount() {
          paymentMethodsService.init();
        },
      } satisfies WidgetLifecycle,
    } as const;
  };
}
